import React, { useEffect, useState } from "react";
import { Routes, Route, NavLink, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LayoutDashboard, FileText, Users, MessageSquare, LogOut, Menu } from "lucide-react";
import { APEX_LOGO } from "@/lib/brand";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NAV = [
  { to: "", label: "Overview", icon: LayoutDashboard },
  { to: "quotes", label: "Quotes", icon: FileText },
  { to: "users", label: "Users", icon: Users },
  { to: "messages", label: "Messages", icon: MessageSquare },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const Sidebar = () => (
    <aside className="w-64 bg-[#0B3D6E] text-slate-300 min-h-screen flex flex-col">
      <Link to="/" className="flex items-center gap-3 px-6 py-5 border-b border-white/10">
        <div className="bg-white rounded-md p-1.5"><img src={APEX_LOGO} alt="Apex" className="h-10 w-auto object-contain" /></div>
        <div className="text-[10px] font-label text-slate-400">Admin Portal</div>
      </Link>
      <nav className="flex-1 py-6 px-3 space-y-1">
        {NAV.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to === "" ? "/portal/admin" : `/portal/admin/${to}`} end={to === ""}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${isActive ? "bg-[#2E7BC7] text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}
            data-testid={`admin-nav-${label.toLowerCase()}`}>
            <Icon className="h-4 w-4" /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-white/10">
        <div className="text-sm text-white font-semibold">{user?.name}</div>
        <div className="text-xs text-slate-400 mb-3">{user?.email}</div>
        <button onClick={async () => { await logout(); nav("/"); }} className="text-xs text-slate-400 hover:text-white flex items-center gap-1" data-testid="admin-logout-btn">
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <div className="hidden md:block"><Sidebar /></div>
      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-gray-200 px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="md:hidden">
              <Sheet><SheetTrigger data-testid="admin-mobile-menu"><Menu className="h-5 w-5 text-[#0B3D6E]" /></SheetTrigger>
                <SheetContent side="left" className="p-0 w-64"><Sidebar /></SheetContent></Sheet>
            </div>
            <h1 className="font-display font-bold text-lg text-[#0B3D6E]">Operations Console</h1>
          </div>
        </header>
        <main className="p-4 md:p-8">
          <Routes>
            <Route index element={<Overview />} />
            <Route path="quotes" element={<Quotes />} />
            <Route path="users" element={<UsersList />} />
            <Route path="messages" element={<AllMessages />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Quotes (24h)" value={stats.quotes_24h ?? "—"} />
        <Stat label="Open quotes" value={stats.open_quotes ?? "—"} />
        <Stat label="Active shipments" value={stats.active_shipments ?? "—"} />
        <Stat label="Customers" value={stats.customers ?? "—"} />
        <Stat label="Carriers" value={stats.carriers ?? "—"} />
        <Stat label="Available loads" value={stats.available_loads ?? "—"} />
        <Stat label="Unpaid invoices" value={stats.unpaid_invoices ?? "—"} />
        <Stat label="Newsletter subs" value={stats.newsletter_subs ?? "—"} />
      </div>
    </div>
  );
}
const Stat = ({ label, value }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-5">
    <div className="font-label text-xs text-slate-500">{label}</div>
    <div className="mt-1 font-display font-black text-2xl text-[#0B3D6E]">{value}</div>
  </div>
);

function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const load = () => api.get("/admin/quotes").then(r => setQuotes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.post(`/admin/quotes/${id}/status`, { status }); toast.success("Updated"); load(); }
    catch { toast.error("Update failed"); }
  };
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="p-5 border-b border-gray-100 font-display font-bold text-[#0B3D6E]">All quotes</div>
      <div className="divide-y divide-gray-100">
        {quotes.map(q => (
          <div key={q.quote_id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid={`admin-quote-${q.quote_id}`}>
            <div className="min-w-0">
              <div className="font-mono text-sm font-semibold text-[#0B3D6E]">{q.quote_id}</div>
              <div className="text-xs text-slate-500 truncate">{q.pickup_city}, {q.pickup_state} → {q.delivery_city}, {q.delivery_state} · {q.equipment_type} · {q.weight_lbs} lbs</div>
              <div className="text-xs text-slate-500">{q.contact_name} · {q.contact_email}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="font-display font-bold text-[#0B3D6E]">Est. ${q.internal_estimate?.toLocaleString?.() ?? "—"}</div>
              <Select value={q.status} onValueChange={(v) => setStatus(q.quote_id, v)}>
                <SelectTrigger className="w-40" data-testid={`admin-quote-status-${q.quote_id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Pending Review", "Quoted", "Accepted", "Declined", "Expired"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
        {!quotes.length && <div className="p-8 text-center text-sm text-slate-500">No quotes yet.</div>}
      </div>
    </div>
  );
}

function UsersList() {
  const [users, setUsers] = useState([]);
  const [target, setTarget] = useState(null);
  const [msg, setMsg] = useState({ subject: "", body: "" });
  useEffect(() => { api.get("/admin/users").then(r => setUsers(r.data)).catch(() => {}); }, []);
  const send = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/messages/send", { user_id: target.user_id, subject: msg.subject, body: msg.body });
      toast.success(`Message sent to ${target.name}`);
      setTarget(null); setMsg({ subject: "", body: "" });
    } catch { toast.error("Send failed"); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="p-5 border-b border-gray-100 font-display font-bold text-[#0B3D6E]">All users</div>
        <div className="divide-y divide-gray-100 max-h-[600px] overflow-auto">
          {users.map(u => (
            <button key={u.user_id} onClick={() => setTarget(u)} className={`w-full text-left p-4 hover:bg-gray-50 ${target?.user_id === u.user_id ? "bg-blue-50" : ""}`} data-testid={`admin-user-${u.user_id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-[#0B3D6E]">{u.name}</div>
                  <div className="text-xs text-slate-500">{u.email} {u.company ? `· ${u.company}` : ""}</div>
                </div>
                <span className={`pill ${u.role === "carrier" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"}`}>{u.role}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="font-display font-bold text-[#0B3D6E]">Message {target ? target.name : "a user"}</div>
        {target ? (
          <form onSubmit={send} className="mt-4 space-y-4" data-testid="admin-message-form">
            <input required placeholder="Subject" value={msg.subject} onChange={(e) => setMsg({ ...msg, subject: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-[#2E7BC7] outline-none" data-testid="admin-msg-subject" />
            <textarea required rows={6} placeholder="Message body" value={msg.body} onChange={(e) => setMsg({ ...msg, body: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-[#2E7BC7] outline-none" data-testid="admin-msg-body" />
            <button className="btn-primary-apex" data-testid="admin-msg-send">Send as Ops</button>
          </form>
        ) : (
          <p className="mt-4 text-sm text-slate-500">Select a user on the left to send a message.</p>
        )}
      </div>
    </div>
  );
}

function AllMessages() {
  const [msgs, setMsgs] = useState([]);
  useEffect(() => { api.get("/admin/messages").then(r => setMsgs(r.data)).catch(() => {}); }, []);
  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <div className="p-5 border-b border-gray-100 font-display font-bold text-[#0B3D6E]">All messages</div>
      <div className="divide-y divide-gray-100 max-h-[700px] overflow-auto">
        {msgs.map(m => (
          <div key={m.msg_id} className="p-4">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-sm text-[#0B3D6E]">{m.subject}</div>
              <span className={`pill ${m.from_ops ? "bg-blue-100 text-blue-800" : "bg-orange-100 text-orange-800"}`}>{m.from_ops ? "Ops" : "User"}</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">User: {m.user_name || m.user_id}</div>
            <p className="text-sm text-slate-700 mt-2">{m.body}</p>
            <div className="text-xs text-slate-400 mt-1">{new Date(m.created_at).toLocaleString()}</div>
          </div>
        ))}
        {!msgs.length && <div className="p-8 text-center text-sm text-slate-500">No messages.</div>}
      </div>
    </div>
  );
}
